import { Paper, Tabs, Tab, Box, Fade, useScrollTrigger, useTheme, useMediaQuery } from "@mui/material";
import { useState, useRef, useEffect } from "react";

interface StickyPricingTabsProps {
    value: number;
    offset?: number;
    onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

export function StickyPricingTabs({ value, offset = 8, onChange }: StickyPricingTabsProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // --- Scroll & Idle Logic ---
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolling(true);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
                setIsScrolling(false);
            }, 1000); // Fade out quicker (1s)
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
    }, []);

    const isPastHero = useScrollTrigger({
        disableHysteresis: true,
        threshold: 400,
    });

    const shouldShowFloating = isPastHero && isScrolling;

    const renderTabs = (isFloating: boolean) => (
        <Paper
            elevation={0}
            sx={{
                p: 0.5,
                borderRadius: 3,
                bgcolor: isFloating ? 'rgba(255, 255, 255, 0.85)' : 'background.paper', // Bump alpha for solidity during fade
                backdropFilter: isFloating ? 'blur(12px)' : 'none',
                WebkitBackdropFilter: isFloating ? 'blur(12px)' : 'none', // Safari fix

                // Compositor hints to reduce pop
                willChange: isFloating ? 'opacity, backdrop-filter' : 'auto',
                transform: isFloating ? 'translateZ(0)' : 'none',

                display: 'flex',
                width: '100%',
                boxShadow: isFloating ? '0 8px 32px rgba(0,0,0,0.12)' : '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: isFloating ? 'rgba(255,255,255,0.4)' : 'transparent',
            }}
        >
            <Tabs
                key={isFloating ? 'floating-tabs' : 'static-tabs'}
                value={value}
                onChange={onChange}
                aria-label="Pricing audience"
                variant={"fullWidth"}
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    width: '100%',
                    "& .MuiTabs-flexContainer": { justifyContent: "center", width: '100%' },
                    "& .MuiTabs-indicator": {
                        height: "100%",
                        borderRadius: 2.5,
                        bgcolor: "primary.main",
                        zIndex: 0,
                        opacity: 0.1,
                        transition: isFloating ? 'none' : undefined,
                    },
                    "& .MuiTab-root": {
                        zIndex: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: isSmallMobile ? "0.80rem" : "0.95rem",
                        py: isMobile ? 0 : 1.5,
                        borderRadius: 2.5,
                        color: "text.secondary",
                        opacity: 1,
                        "&:hover": {
                            color: "text.primary",
                            opacity: 1,
                        },
                        "&.Mui-selected": {
                            color: "primary.main",
                            opacity: 1,
                        },
                        paddingLeft: isSmallMobile ? 1 : 2,
                        paddingRight: isSmallMobile ? 1 : 2,
                        minWidth: isSmallMobile ? 0 : undefined,
                        whiteSpace: 'nowrap',
                    },
                }}
            >
                <Tab
                    label={isMobile ? "Professionals" : "Professionals & Care Teams"}
                    {...(!isFloating && { id: 'pricing-tab-professionals', 'aria-controls': 'pricing-panel-professionals' })}
                />
                <Tab
                    label={isMobile ? "Patients" : "Individuals & Families"}
                    {...(!isFloating && { id: 'pricing-tab-individuals', 'aria-controls': 'pricing-panel-individuals' })}
                />
                <Tab
                    label={isMobile ? "Case Managers" : "Case Management"}
                    {...(!isFloating && { id: 'pricing-tab-complete', 'aria-controls': 'pricing-panel-complete' })}
                />
            </Tabs>
        </Paper>
    );

    return (
        <>
            {/* 1. Static Tabs (In Flow) */}
            <Box sx={{
                position: 'relative',
                zIndex: 10,
                mt: -offset,
                mb: 4,
                maxWidth: 640,
                mx: "auto",
                px: 2,
            }}>
                {renderTabs(false)}
            </Box>

            {/* 2. Floating Tabs (Fixed, Show on Scroll) */}
            <Fade
                in={shouldShowFloating}
                timeout={600}
                easing={{ enter: "cubic-bezier(0.4, 0, 0.2, 1)", exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
                mountOnEnter
                unmountOnExit
            >
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'fixed',
                        top: { xs: 70, md: 80 },
                        left: 0,
                        right: 0,
                        zIndex: 1100,
                        maxWidth: 640,
                        mx: "auto",
                        px: 2,
                    }}>
                    {renderTabs(true)}
                </Box>
            </Fade>
        </>
    );
}
